import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, BellOff, Bell, Check, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { API_BASE_URL, BACKEND_URL } from '../utils/network.js';
import { localizeMessage } from '../utils/toast.js';
import Spinner from './ui/Spinner.jsx';
import './FollowSettings.css';

const defaultAvatar = "/nkarner/20260110_1932_Image Generation_remix_01kem8h20rewa81f8h55ncqmm9 (2).png";

function MutedUsers() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [mutedUsers, setMutedUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [unmutingId, setUnmutingId] = useState(null);

  const getToken = () => localStorage.getItem('token');

  useEffect(() => { fetchMutedUsers(); }, []);

  const fetchMutedUsers = async () => {
    try {
      setLoading(true);
      const token = getToken();
      if (!token) { setError(t('blockedMuted.pleaseLogin')); return; }
      const response = await fetch(`${API_BASE_URL}/users/muted`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (response.ok && data.success) setMutedUsers(data.mutedUsers || []);
      else setError(data.error || t('blockedMuted.failedLoadMuted'));
    } catch {
      setError(t('blockedMuted.failedLoadMuted'));
    } finally {
      setLoading(false);
    }
  };

  const handleUnmute = async (userId) => {
    try {
      setUnmutingId(userId);
      const token = getToken();
      const response = await fetch(`${API_BASE_URL}/users/${userId}/unmute`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (response.ok && data.success) {
        setMutedUsers((prev) => prev.filter((u) => String(u._id) !== String(userId)));
        setSuccessMessage(t('blockedMuted.userUnmuted'));
        setTimeout(() => setSuccessMessage(null), 3000);
      } else {
        setError(data.error || t('blockedMuted.failedUnmute'));
      }
    } catch {
      setError(t('blockedMuted.failedUnmute'));
    } finally {
      setUnmutingId(null);
    }
  };

  const getImageUrl = (url) => {
    if (!url || url === 'null' || url === 'undefined') return defaultAvatar;
    if (url.startsWith('http')) return url;
    return `${BACKEND_URL}${url.startsWith('/') ? '' : '/'}${url}`;
  };

  return (
    <div className="follow-settings-container">
      <button onClick={() => navigate('/settings')} style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', color: '#32614d', fontWeight: 600, fontSize: 14, cursor: 'pointer', marginBottom: 16, padding: 0 }}>
        <ArrowLeft size={16} /> {t('blockedMuted.backToSettings')}
      </button>

      <div className="settings-header">
        <h2>{t('blockedMuted.mutedTitle')}</h2>
        <p className="settings-subtitle">
          {t('blockedMuted.mutedSubtitle')}
        </p>
      </div>

      {error && (
        <div className="error-message"><X size={20} /><span>{localizeMessage(error)}</span></div>
      )}
      {successMessage && (
        <div className="success-message"><Check size={20} /><span>{successMessage}</span></div>
      )}

      {loading ? (
        <div className="loading-state">
          <Spinner size={40} />
          <p>{t('blockedMuted.loading')}</p>
        </div>
      ) : mutedUsers.length === 0 ? (
        <div className="settings-card" style={{ textAlign: 'center', padding: '48px 24px' }}>
          <BellOff size={40} color="#ccc" style={{ marginBottom: 12 }} />
          <h3 style={{ color: '#555', margin: '0 0 6px' }}>{t('blockedMuted.noMuted')}</h3>
          <p style={{ color: '#aaa', fontSize: 14, margin: 0 }}>{t('blockedMuted.mutedEmptyHint')}</p>
        </div>
      ) : (
        <div className="settings-card" style={{ padding: 0, overflow: 'hidden' }}>
          {mutedUsers.map((u) => (
            <div key={u._id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 20px', borderBottom: '1px solid #f0f0f0' }}>
              <img src={getImageUrl(u.profileImage)} alt="" style={{ width: 44, height: 44, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} onError={(e) => { e.target.src = defaultAvatar; }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: 14, color: '#1a1a1a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {u.organizationName || u.name || u.username}
                </div>
                {u.username && <div style={{ fontSize: 12, color: '#888' }}>@{u.username}</div>}
              </div>
              <button onClick={() => handleUnmute(u._id)} disabled={unmutingId === u._id}
                style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', background: '#f0f9f4', color: '#32614d', border: '1.5px solid #32614d', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', opacity: unmutingId === u._id ? 0.6 : 1, flexShrink: 0 }}>
                <Bell size={14} /> {unmutingId === u._id ? t('blockedMuted.unmuting') : t('blockedMuted.unmute')}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default MutedUsers;
